import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';

const Settings = ({ user, settings, onSettingsUpdated }) => {
  const [companyName, setCompanyName] = useState(settings?.company_name || '');
  const [logoFile, setLogoFile] = useState(null);
  const [showSmtp, setShowSmtp] = useState(false);
  const [smtp, setSmtp] = useState({
    smtp_host: settings?.smtp_host || '',
    smtp_port: settings?.smtp_port || '',
    smtp_secure: settings?.smtp_secure ? '1' : '0',
    smtp_user: settings?.smtp_user || '',
    smtp_pass: '',
    smtp_from: settings?.smtp_from || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApprovalTypes, setShowApprovalTypes] = useState(false);
  const [approvalTypes, setApprovalTypes] = useState([]);
  const [newApprovalType, setNewApprovalType] = useState('');

  useEffect(() => {
    setCompanyName(settings?.company_name || '');
    setSmtp({
      smtp_host: settings?.smtp_host || '',
      smtp_port: settings?.smtp_port || '',
      smtp_secure: settings?.smtp_secure ? '1' : '0',
      smtp_user: settings?.smtp_user || '',
      smtp_pass: '',
      smtp_from: settings?.smtp_from || ''
    });
  }, [settings?.company_name, settings?.smtp_host, settings?.smtp_port, settings?.smtp_secure, settings?.smtp_user, settings?.smtp_from]);

  const canEdit = useMemo(() => user?.role === 'ADMIN', [user?.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const form = new FormData();
      form.append('company_name', companyName);
      if (logoFile) {
        form.append('logo', logoFile);
      }

      await api.put('/api/settings', form);
      setLogoFile(null);
      setSuccess('Settings updated successfully');

      const refreshed = await api.get('/api/settings');
      onSettingsUpdated(refreshed.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovalTypes = async () => {
    try {
      const res = await api.get('/api/approval-types');
      setApprovalTypes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setApprovalTypes([]);
    }
  };

  const createApprovalType = async () => {
    const name = String(newApprovalType || '').trim();
    if (!name) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/api/approval-types', { name });
      setNewApprovalType('');
      await loadApprovalTypes();
      setSuccess('Approval type created');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create approval type');
    } finally {
      setLoading(false);
    }
  };

  const toggleApprovalType = async (t) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.put(`/api/approval-types/${t.id}`, { is_active: t.is_active ? 0 : 1 });
      await loadApprovalTypes();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update approval type');
    } finally {
      setLoading(false);
    }
  };

  const handleSmtpChange = (e) => {
    setSmtp({
      ...smtp,
      [e.target.name]: e.target.value
    });
  };

  const saveSmtp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const form = new FormData();
      form.append('company_name', companyName || settings?.company_name || 'Pipeline Management');
      form.append('smtp_host', smtp.smtp_host);
      form.append('smtp_port', smtp.smtp_port);
      form.append('smtp_secure', smtp.smtp_secure);
      form.append('smtp_user', smtp.smtp_user);
      if (smtp.smtp_pass) {
        form.append('smtp_pass', smtp.smtp_pass);
      }
      form.append('smtp_from', smtp.smtp_from);

      await api.put('/api/settings', form);
      setSuccess('SMTP settings saved');
      setShowSmtp(false);

      const refreshed = await api.get('/api/settings');
      onSettingsUpdated(refreshed.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/settings/test-email');
      setSuccess('Test email sent to your login email');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'ADMIN') {
    return (
      <div className="glass-card p-8">
        <h1 className="text-2xl font-bold mb-2">Settings</h1>
        <p className="text-secondary">Insufficient permissions.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Admin Settings</h1>
      </div>

      <div className="glass-card p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="error-message">
              <span className="error-text">{error}</span>
            </div>
          )}

          {success && (
            <div className="success-message">
              <span className="success-text">{success}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Company / Portal Name</label>
            <input
              type="text"
              className="glass-input w-full"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
            <p className="text-xs text-tertiary mt-2">Used in header, login screen, and browser tab title.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Company Logo</label>
            {settings?.logo_url ? (
              <div className="flex items-center gap-4 mb-3">
                <img src={settings.logo_url} alt="Company logo" className="brand-logo-preview" />
                <div className="text-sm text-tertiary">Current logo</div>
              </div>
            ) : (
              <div className="text-sm text-tertiary mb-3">No logo uploaded</div>
            )}

            <input
              type="file"
              accept="image/*"
              className="glass-input w-full"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
            />
            <p className="text-xs text-tertiary mt-2">PNG/JPG recommended. After saving, it will appear in the header and login.</p>
          </div>

          <div className="flex gap-2">
            <button type="submit" className="glass-button primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
            <button type="button" className="glass-button" onClick={() => setShowSmtp(true)}>
              SMTP Settings
            </button>
            <button type="button" className="glass-button" onClick={async () => { setShowApprovalTypes(true); await loadApprovalTypes(); }}>
              Approval Types
            </button>
            <button type="button" className="glass-button" onClick={sendTestEmail} disabled={loading}>
              Send Test Email
            </button>
          </div>
        </form>
      </div>

      {showSmtp && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h2 className="text-xl font-semibold">SMTP Email Settings</h2>
              <button type="button" className="glass-button" onClick={() => setShowSmtp(false)}>Close</button>
            </div>

            <div className="modal-body">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">SMTP Host</label>
                  <input name="smtp_host" value={smtp.smtp_host} onChange={handleSmtpChange} className="glass-input w-full" placeholder="smtp.gmail.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">SMTP Port</label>
                  <input name="smtp_port" value={smtp.smtp_port} onChange={handleSmtpChange} className="glass-input w-full" placeholder="587" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Secure (TLS)</label>
                  <select name="smtp_secure" value={smtp.smtp_secure} onChange={handleSmtpChange} className="glass-select w-full">
                    <option value="0">No</option>
                    <option value="1">Yes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">SMTP User</label>
                  <input name="smtp_user" value={smtp.smtp_user} onChange={handleSmtpChange} className="glass-input w-full" placeholder="user@company.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">SMTP Password</label>
                  <input name="smtp_pass" type="password" value={smtp.smtp_pass} onChange={handleSmtpChange} className="glass-input w-full" placeholder="(leave blank to keep existing)" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">From Address</label>
                  <input name="smtp_from" value={smtp.smtp_from} onChange={handleSmtpChange} className="glass-input w-full" placeholder="no-reply@company.com" />
                </div>
              </div>
              <p className="text-xs text-tertiary mt-4">Email reminders are sent when reminders are due and the user opens the dashboard (or any screen calling alerts).</p>
            </div>

            <div className="modal-footer">
              <button type="button" className="glass-button primary" onClick={saveSmtp} disabled={loading}>
                {loading ? 'Saving...' : 'Save SMTP'}
              </button>
              <button type="button" className="glass-button" onClick={sendTestEmail} disabled={loading}>
                Send Test Email
              </button>
            </div>
          </div>
        </div>
      )}

      {showApprovalTypes && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h2 className="text-xl font-semibold">Approval Types</h2>
              <button type="button" className="glass-button" onClick={() => setShowApprovalTypes(false)}>Close</button>
            </div>

            <div className="modal-body">
              <div className="flex gap-2">
                <input className="glass-input w-full" value={newApprovalType} onChange={(e) => setNewApprovalType(e.target.value)} placeholder="New type name" />
                <button type="button" className="glass-button primary" onClick={createApprovalType} disabled={loading}>
                  Add
                </button>
              </div>

              <div className="grid gap-2 mt-4">
                {approvalTypes.length === 0 ? (
                  <div className="text-sm text-secondary">No approval types found</div>
                ) : (
                  approvalTypes.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="font-medium">{t.name}</div>
                      <button type="button" className={`glass-button ${t.is_active ? '' : 'danger'}`} onClick={() => toggleApprovalType(t)} disabled={loading}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="glass-button" onClick={async () => { await loadApprovalTypes(); }} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
